import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import B2BPricingSection from '@/components/pricing/B2BPricingSection';
import Logo from '@/components/Logo';

const BrandsLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  const handleLearnMore = () => {
    navigate('/how-it-works');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo />
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button 
                  onClick={() => navigate('/?site=individual')}
                  className="font-sans px-3 py-1 rounded-md transition-colors text-black hover:bg-white hover:shadow-sm"
                >
                  Individual Use
                </button>
                <button 
                  className="font-sans px-3 py-1 rounded-md bg-black text-white shadow-sm"
                >
                  For Brands
                </button>
              </div>
              <button 
                onClick={() => navigate('/pricing')}
                className="font-sans text-black hover:text-gray-600 transition-colors"
              >
                Pricing
              </button>
              {user ? (
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="font-sans bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Go to Dashboard
                </button>
              ) : (
                <div className="flex gap-2">
                  <button 
                    onClick={() => navigate('/auth')}
                    className="font-sans text-black hover:text-gray-600 transition-colors px-4 py-2"
                  >
                    Sign In
                  </button>
                  <button 
                    onClick={handleGetStarted}
                    className="font-sans bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Get Started
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 md:pt-48 bg-white">
        <div className="relative z-10 max-w-6xl mx-auto text-center w-full">
          <div className="mb-6 sm:mb-8 md:mb-12">
            {/* Main headline for B2B */}
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-normal text-black leading-tight mb-2 sm:mb-3 md:mb-4 px-2 text-center">
              Smart Social Listening
            </h1>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-normal text-black leading-tight mb-4 sm:mb-6 md:mb-8 px-2 text-center">
              + Protection for Growing Brands
            </h2>
            
            {/* B2B focused tagline */}
            <div className="max-w-4xl mx-auto mb-6 sm:mb-8 md:mb-12 px-4">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8">
                <p className="font-sans text-base sm:text-lg md:text-xl lg:text-2xl text-black leading-relaxed font-light tracking-wider-lg">
                  Monitor brand mentions. Protect reputation. Drive growth.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 sm:mb-12 md:mb-16 px-4">
              <button 
                onClick={handleGetStarted}
                className="font-sans bg-black hover:bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 text-lg min-w-48 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Start Free Trial
              </button>
              <button 
                onClick={handleLearnMore}
                className="font-sans border-2 border-black hover:border-gray-800 bg-white hover:bg-gray-50 text-black hover:text-gray-800 px-8 py-4 rounded-xl font-semibold transition-all duration-300 text-lg min-w-48"
              >
                See How It Works
              </button>
            </div>
          </div>
          
          {/* B2B Features Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-12 mt-8 sm:mt-12 md:mt-20 px-4">
            {/* Brand Intelligence */}
            <div className="text-center">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-normal text-black mb-4">
                  Brand Intelligence
                </h3>
                <p className="font-sans text-black leading-relaxed text-base font-light">
                  Monitor brand mentions, competitor activity, and industry conversations across all major platforms in real-time.
                </p>
              </div>
            </div>

            {/* Crisis Prevention */}
            <div className="text-center">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-normal text-black mb-4">
                  Crisis Prevention
                </h3>
                <p className="font-sans text-black leading-relaxed text-base font-light">
                  Get instant alerts for potential brand threats, negative sentiment spikes, and reputation risks before they escalate.
                </p>
              </div>
            </div>
            
            {/* Growth Analytics */}
            <div className="text-center">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                  <span className="text-2xl">📈</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-normal text-black mb-4">
                  Growth Analytics
                </h3>
                <p className="font-sans text-black leading-relaxed text-base font-light">
                  Identify trending topics, viral content opportunities, and audience insights to fuel your marketing strategy.
                </p>
              </div>
            </div>
          </div>
          
          {/* Social Proof Section */}
          <div className="mt-16 sm:mt-20 md:mt-24 px-4">
            <div className="bg-gray-50 rounded-2xl p-8 sm:p-12">
              <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-normal text-black mb-8 text-center">
                Trusted by Growing Brands
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-black mb-2">50K+</div>
                  <div className="font-sans text-gray-600">Brand mentions monitored daily</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-black mb-2">24/7</div>
                  <div className="font-sans text-gray-600">Real-time brand protection</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* B2B Pricing Section */}
          <B2BPricingSection onGetStarted={handleGetStarted} />
          
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Logo />
            <p className="font-sans text-gray-600 mt-4">
                Smart social listening + protection for growing brands
            </p>
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="font-sans text-sm text-gray-500">
                © 2024 SocialScoop.ai. All rights reserved. Smart Social Listening and Brand Protection Technology.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BrandsLanding;